import { type Maybe, type ValueEvent } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { type DialogOptions } from '@adornis/dialog/x-plain-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { cloneLogistikOrderItem } from '@chancenlogistik/client/helper.js';
import {
  LogistikOrder,
  LogistikOrderItem
} from '@chancenwerk/db/LogistikOrder.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenRoleAspect } from '@chancenwerk/db/semantics/ChancenRoleAspect.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import '../../../../../src/client/components/semantics/chancen-component-semantic-user-picker.js';
import '../logistik-component-order-item-card.js';

/**
 * @customElement logistik-dialog-split-order
 */
@customElement('logistik-dialog-split-order')
export class LogistikDialogSplitOrder extends ChromiumDialog<void> {
  static override get element_name() {
    return 'logistik-dialog-split-order';
  }

  @state() public order = new RXController(this, new LogistikOrder({}) as LogistikOrder | null);

  @state() private readonly _orderItems = new RXController<LogistikOrderItem[]>(this, [] as LogistikOrderItem[]);
  @state() private readonly _newOrderItems = new RXController<LogistikOrderItem[]>(this, [] as LogistikOrderItem[]);

  @state() private readonly _isLoading = new RXController(this, false);

  @state() private readonly _selectedProcessor = new RXController(this, '');
  @state() private readonly _selectedProcessorNew = new RXController(this, '');

  override connectedCallback(): void {
    super.connectedCallback();

    this.order.observable.subscribe(order => {
      if (order) {
        this._orderItems.next(order.items.filter(item => item instanceof LogistikOrderItem) as LogistikOrderItem[]);
      }
    });
    if (this.order.value!.processorID) {
      this._selectedProcessor.next(this.order.value!.processorID);
    }
  }

  override content() {
    if (!this.order.value) return html`<cr-text>Order not found</cr-text>`;
    return html`
      <cr-flex space="md" padding="md">
        ${this._renderDialogHeader()} ${this._renderOrderItemsDND()} ${this._renderDialogFooter()}
      </cr-flex>
    `;
  }

  private _renderDialogFooter() {
    return html`
      <cr-flex horizontal space="md" padding="md" ${css({ placeContent: 'flex-end' })}>
        <cr-button mode="outline" @click=${() => this._cancel()}> Abbrechen </cr-button>
        <cr-button
          ?loading=${this._isLoading.value}
          @click=${async () => {
            if (this._newOrderItems.value.length === 0) {
              void ChromiumSnackbar.show('Bitte füge mindestens ein Produkt zur neuen Bestellung hinzu.');
              return;
            }
            if (!(await ChromiumDialog.confirm('Sind Sie sicher, dass Sie die Bestellung aufteilen möchten?'))) return;
            this._isLoading.next(true);
            await LogistikOrder.splitOrder(
              this.order.value!._id,
              this._newOrderItems.value,
              this._selectedProcessor.value,
              this._selectedProcessorNew.value,
            )();
            this._isLoading.next(false);
            await this.close();
          }}
        >
          Bestellung aufteilen
        </cr-button>
      </cr-flex>
    `;
  }

  private _cancel() {
    this.order.value?.revert();
    this.close();
  }

  private _renderDialogHeader() {
    return html`
      <cr-flex horizontal space="md" padding="md" space-between>
        <cr-text bold>Bestellung aufteilen</cr-text>
        <cr-button mode="icon" icon="close" @click=${() => this._cancel()}></cr-button>
      </cr-flex>
    `;
  }

  private _renderOrderItemsDND() {
    return html`
      <cr-grid columns="1fr 1fr" space="md" padding="md">
        <cr-flex space="md">
          <cr-text bold>Aktuelle Bestellung</cr-text>

          <chancen-component-semantic-user-picker
            ${css({ flex: '1', alignItems: 'center' })}
            placeholder="Bearbeiter auswählen"
            .filter=${new ChancenwerkUserFilter({
              aspects: [new ChancenRoleAspect({ identifier: 'role', value: Role.LOGISTICS_STAFF })],
            })}
            .valueID=${this._selectedProcessor.value}
            @value-picked=${(e: ValueEvent<string>) => {
              this._selectedProcessor.next(e.detail.value);
              this.order.value!.processorID = e.detail.value;
              this.requestUpdate();
            }}
          ></chancen-component-semantic-user-picker>
          <x-drag-drop
            drag-mode="freeMove"
            allow-from="logistik-component-order-item-card"
            dropzone-accept-selector="logistik-component-order-item-card"
            child-selector="logistik-component-order-item-card"
            ${css({ height: '100%', width: '100%' })}
            @dropped=${async (e: CustomEvent<HTMLElement>) =>
              this._moveItemBetweenOrders(e, this._newOrderItems, this._orderItems)}
          >
            <cr-flex
              space="sm"
              padding="md"
              ${css({ width: '100%', border: '3px dashed #E5E5E5', borderRadius: this.spacing.sm })}
            >
              ${repeat(
                this._orderItems.value,
                item => item._id,
                item => html`
                  <logistik-component-order-item-card
                    .item=${item}
                    class="handle"
                    id=${item._id}
                    key=${item._id}
                    @quantity-changed=${(e: CustomEvent<{ quantity: number }>) =>
                      this._handleQuantityChanged(item, e.detail.quantity, this._orderItems, this._newOrderItems)}
                  ></logistik-component-order-item-card>
                `,
              )}
            </cr-flex>
          </x-drag-drop>
        </cr-flex>
        <cr-flex space="md">
          <cr-text bold>Neue Bestellung</cr-text>
          <chancen-component-semantic-user-picker
            ${css({ flex: '1', alignItems: 'center' })}
            placeholder="Bearbeiter auswählen"
            .filter=${new ChancenwerkUserFilter({
              aspects: [new ChancenRoleAspect({ identifier: 'role', value: Role.LOGISTICS_STAFF })],
            })}
            .valueID=${this._selectedProcessorNew.value}
            @value-picked=${(e: ValueEvent<Maybe<ChancenwerkUser>>) => {
              this._selectedProcessorNew.next(e.detail.value?._id ?? '');
            }}
          ></chancen-component-semantic-user-picker>
          <x-drag-drop
            drag-mode="freeMove"
            allow-from="logistik-component-order-item-card"
            dropzone-accept-selector="logistik-component-order-item-card"
            child-selector="logistik-component-order-item-card"
            ${css({ height: '100%', width: '100%' })}
            @dropped=${async (e: CustomEvent<HTMLElement>) =>
              this._moveItemBetweenOrders(e, this._orderItems, this._newOrderItems)}
          >
            <cr-flex
              space="sm"
              padding="md"
              ${css({ width: '100%', border: '3px dashed #E5E5E5', borderRadius: this.spacing.sm })}
            >
              ${repeat(
                this._newOrderItems.value,
                item => item._id,
                item => html`
                  <logistik-component-order-item-card
                    .item=${item}
                    class="handle"
                    id=${item._id}
                    key=${item._id}
                    @quantity-changed=${(e: CustomEvent<{ quantity: number }>) =>
                      this._handleQuantityChanged(item, e.detail.quantity, this._newOrderItems, this._orderItems)}
                  ></logistik-component-order-item-card>
                `,
              )}
            </cr-flex>
          </x-drag-drop>
        </cr-flex>
      </cr-grid>
    `;
  }

  private _moveItemBetweenOrders(
    e: CustomEvent<HTMLElement>,
    src: RXController<LogistikOrderItem[]>,
    dst: RXController<LogistikOrderItem[]>,
  ) {
    if (!src.value || !dst.value || !e.detail.id) return;
    const srcItem = src.value.find(i => i._id === e.detail.id);
    if (!srcItem) return;

    const dstItem = dst.value.find(i => i.productID === srcItem.productID);
    if (dstItem) {
      dstItem.quantity += srcItem.quantity;
      // Remove item from src order
      src.next(src.value.filter(i => i._id !== srcItem._id));
      // Add item to dst order
      dst.next(dst.value.map(i => (i._id === dstItem._id ? dstItem : i)));
    } else {
      src.next(src.value.filter(i => i._id !== srcItem._id));
      dst.next(dst.value.concat(srcItem));
    }
  }

  private _handleQuantityChanged(
    srcItem: LogistikOrderItem,
    desiredQuantity: number,
    src: RXController<LogistikOrderItem[]>,
    dst: RXController<LogistikOrderItem[]>,
  ) {
    if (!src.value.find(i => i._id === srcItem._id) || desiredQuantity <= 0 || srcItem.quantity === desiredQuantity) {
      this.requestUpdate();
      return;
    }

    const setDesiredSrcItemQuantity = () => {
      srcItem.quantity = desiredQuantity;
      src.next(src.value.map(i => (i._id === srcItem._id ? srcItem : i)));
    };
    if (desiredQuantity < srcItem.quantity) {
      const difference = srcItem.quantity - desiredQuantity;
      const dstItem = dst.value.find(i => i.productID === srcItem.productID);
      if (dstItem) {
        dstItem.quantity += difference;
        dst.next(dst.value.map(i => (i._id === dstItem._id ? dstItem : i)));
        setDesiredSrcItemQuantity();
      } else {
        const clonedItem = cloneLogistikOrderItem(srcItem);
        clonedItem.quantity = difference;
        dst.next(dst.value.concat(clonedItem));
        setDesiredSrcItemQuantity();
      }
    }
    // desiredQuantity > srcItem.quantity
    else {
      const difference = desiredQuantity - srcItem.quantity;
      const dstItem = dst.value.find(i => i.productID === srcItem.productID);
      if (!dstItem) {
        this.requestUpdate();
        return;
      }

      if (dstItem.quantity > difference) {
        dstItem.quantity -= difference;
        dst.next(dst.value.map(i => (i._id === dstItem._id ? dstItem : i)));
        setDesiredSrcItemQuantity();
      } else {
        dst.next(dst.value.filter(i => i._id !== dstItem._id));
        srcItem.quantity += dstItem.quantity;
        src.next(src.value.map(i => (i._id === srcItem._id ? srcItem : i)));
      }
    }
  }

  public override getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options as Required<DialogOptions>);

    if (!options.draggable) {
      wrapperElem.style.setProperty('max-width', '90vw');
      wrapperElem.style.setProperty('max-height', '100vh');
      wrapperElem.style.setProperty('min-width', '60vw');
    }
    return wrapperElem;
  }
}
